import { useEffect, useMemo, useRef, useState } from 'react'
import { useIsWidgetPreview } from '@/contexts/WidgetPreviewContext'

/**
 * How long the config form is left alone before a draft is queried. Long
 * enough that clicking through three metrics costs one request, short enough
 * that the preview still feels live.
 */
export const WIDGET_DRAFT_DEBOUNCE_MS = 400

/** Suffix that separates draft query keys from the saved widget's. */
export const DRAFT_KEY_SUFFIX = '-draft'

/**
 * A stable-enough identity for a widget, used to tell "the same draft again"
 * from "a real edit". Config objects are plain JSON built by
 * `buildWidgetConfigJson`, so key order is deterministic.
 *
 * @param {Object|null|undefined} widget
 * @returns {string}
 */
function widgetSignature(widget) {
  try {
    return JSON.stringify(widget ?? null)
  } catch {
    // A widget carrying something circular is not previewable anyway; fall
    // back to an identity that always looks new rather than throwing.
    return String(Date.now())
  }
}

/**
 * The widget as it was `delayMs` ago, ignoring edits that are still arriving.
 *
 * Returns the value unchanged when `delayMs` is 0, so a caller that is already
 * receiving a settled draft does not lag a second debounce behind it.
 *
 * @param {Object|null|undefined} value
 * @param {number} delayMs
 * @returns {Object|null|undefined}
 */
function useDebouncedWidget(value, delayMs) {
  const [settled, setSettled] = useState(value)

  const latest = useRef(value)
  latest.current = value

  const signature = useMemo(
    () => (delayMs > 0 ? widgetSignature(value) : ''),
    [value, delayMs]
  )

  useEffect(() => {
    if (delayMs <= 0) return undefined

    const timer = setTimeout(() => {
      // Re-check rather than always assigning: the draft object is rebuilt on
      // every keystroke, so an unchanged config would otherwise hand the query
      // a new identity and re-render for nothing.
      setSettled((previous) => (widgetSignature(previous) === signature ? previous : latest.current))
    }, delayMs)

    return () => clearTimeout(timer)
  }, [signature, delayMs])

  return delayMs > 0 ? settled : value
}

/**
 * Resolve how a widget data hook should behave for this call.
 *
 * The third argument of `useWidgetData`/`useNumberWidgetData` was a boolean
 * `enabled`; it now also accepts `{ enabled, draft }`. Both forms are read here
 * so the two hooks cannot drift.
 *
 * `draft: true` means the caller owns an unsaved form: the widget is debounced
 * and the query is namespaced away from the saved widget's cache key. Being
 * merely *inside* a preview (the chart component under `WidgetPreview`) gets
 * the namespace but not a second debounce.
 *
 * @param {Object|null|undefined} widget
 * @param {boolean|{enabled?: boolean, draft?: boolean}} [optionsOrEnabled=true]
 * @returns {{isDraft: boolean, effectiveWidget: Object|null|undefined, enabled: boolean}}
 */
export function useWidgetDraftMode(widget, optionsOrEnabled = true) {
  const isInsidePreview = useIsWidgetPreview()

  const options =
    optionsOrEnabled && typeof optionsOrEnabled === 'object'
      ? optionsOrEnabled
      : { enabled: optionsOrEnabled !== false }

  const ownsDraft = options.draft === true
  const isDraft = ownsDraft || (options.draft !== false && isInsidePreview)

  const debounced = useDebouncedWidget(widget, ownsDraft ? WIDGET_DRAFT_DEBOUNCE_MS : 0)

  return {
    isDraft,
    effectiveWidget: debounced,
    enabled: options.enabled !== false,
  }
}

/**
 * How long a draft result stays fresh, and how long it is kept once nothing is
 * watching it. Short enough that a closed dialog leaves nothing behind for
 * long, but NOT zero.
 *
 * The preview pane and the widget it renders are two observers of the same
 * query, and the second subscribes a moment after the first. At a staleTime of
 * 0 the result is already stale by then, so the second observer refetches on
 * mount and one edit costs two identical requests; at a gcTime of 0 the query
 * is dropped in the gap between the two, with the same effect. Neither is
 * needed for isolation - that is the key namespace's job - and a draft's whole
 * config is in its key, so a real edit always produces a different query.
 */
const DRAFT_CACHE_WINDOW_MS = 30 * 1000

/**
 * React Query options that keep a draft result out of the saved widget's cache
 * and drop it shortly after the dialog closes.
 *
 * Drafts also do not retry. A saved widget retries because a blip should not
 * blank a dashboard; a preview is being watched by somebody holding the form,
 * for whom a silent second-long pause reads as the preview being broken.
 *
 * @param {boolean} isDraft
 * @returns {{staleTime: number, gcTime?: number, retry: number}}
 */
export function draftCacheOptions(isDraft) {
  return isDraft
    ? { staleTime: DRAFT_CACHE_WINDOW_MS, gcTime: DRAFT_CACHE_WINDOW_MS, retry: 0 }
    : { staleTime: 5 * 60 * 1000, retry: 1 }
}
